<template>
  <main class="scheme-page">
    <section class="selection-card">
      <div class="section-heading">
        <div>
          <span class="eyebrow">方案条件</span>
          <h2>选择地块与目标产量</h2>
        </div>
      </div>
      <label>地块名称
        <el-select v-model="landId" placeholder="请选择地块" @change="onLandChange">
          <el-option v-for="land in lands" :key="land.id" :label="land.name" :value="land.id" />
        </el-select>
      </label>
      <label>目标产量
        <el-select v-model="yieldId" :disabled="!landId" placeholder="请选择目标产量">
          <el-option v-for="item in yields" :key="item.id" :label="formatYield(item)" :value="item.id" />
        </el-select>
      </label>
      <el-button type="primary" :loading="creating" @click="createScheme">{{ scheme ? '重新生成方案' : '生成水肥方案' }}</el-button>
      <p v-if="errorMsg" class="request-error">{{ errorMsg }}</p>
    </section>

    <template v-if="isValidScheme">
      <section class="scheme-title">
        <span>作物水肥一体化方案</span>
        <b>{{ scheme.foundation && scheme.foundation.cropName || selectedLandName }}</b>
        <small>{{ scheme.foundation && scheme.foundation.placeName }}</small>
      </section>

      <section class="nutrient-card">
        <div class="card-caption"><span>投肥总量</span><small>推荐区间</small></div>
        <div class="summary-grid">
          <div><span>纯氮 (N)</span><strong>{{ fertilizerRange('N') }}</strong><small>kg/亩</small></div>
          <div><span>纯磷 (P₂O₅)</span><strong>{{ fertilizerRange('P') }}</strong><small>kg/亩</small></div>
          <div><span>纯钾 (K₂O)</span><strong>{{ fertilizerRange('K') }}</strong><small>kg/亩</small></div>
        </div>
      </section>

      <section v-for="card in fertilizerCards" :key="card.key" class="fertilizer-card">
        <div class="card-caption"><span>{{ card.title }}</span><small>{{ recalculating ? '正在重新计算…' : '修改后自动重新计算' }}</small></div>
        <div v-for="type in ['N', 'P', 'K']" :key="type" class="fertilizer-row">
          <div>
            <span>{{ { N: '氮肥', P: '磷肥', K: '钾肥' }[type] }}</span>
            <el-select v-model="card.values[type].id" @change="markFertilizerChanged(card.key)">
              <el-option v-for="item in fertilizers[type]" :key="item.id" :label="item.fertilizerName" :value="item.id" />
            </el-select>
          </div>
          <strong class="fertilizer-amount">
            <small>推荐用量</small>
            <b>{{ card.values[type].amount || '-' }}</b>
            <em>kg/亩</em>
          </strong>
        </div>
      </section>

      <section class="water-card">
        <div><span>亩灌水总量</span><strong>{{ range(scheme.totalNPK.waterMin, scheme.totalNPK.waterMax) }}</strong><small>m³/亩</small></div>
        <div><span>总灌溉次数</span><strong>{{ totalWaterCount }}</strong><small>次</small></div>
      </section>

      <section class="period-card">
        <h2>生育期水肥建议</h2>
        <article v-for="(period, index) in periods" :key="index" class="period-row">
          <h3>{{ index + 1 }}. {{ period.name || '生育期' }}</h3>
          <div><span>滴水次数</span><b>{{ value(period.waterNum) }} 次</b></div>
          <div><span>每次滴水量</span><b>{{ range(period.requiredWaterMin, period.requiredWaterMax) }} m³/亩</b></div>
          <div><span>氮 / 磷 / 钾</span><b>{{ range(period.periodsNMin, period.periodsNMax) }} / {{ range(period.periodsPMin, period.periodsPMax) }} / {{ range(period.periodsKMin, period.periodsKMax) }} kg/亩</b></div>
        </article>
      </section>

      <footer class="action-bar">
        <el-button v-if="baseChanged || pursueChanged" plain :loading="recalculating" @click="recalculate">重新计算</el-button>
        <el-button plain :loading="creating" @click="createScheme">重新生成</el-button>
        <el-button type="success" :loading="saving" @click="saveScheme">保存方案</el-button>
      </footer>
    </template>
    <p v-else-if="loaded" class="empty">请选择地块和目标产量后生成方案</p>
  </main>
</template>

<script>
import { createLandScheme, fertilizerFindAll, getLand, updateBase, updateTopdressing, wfschemeSave, yieldFindAll } from '../utils/getxx'

export default {
  data() {
    return {
      lands: [], yields: [], landId: '', yieldId: '', scheme: null, loaded: false, errorMsg: '',
      creating: false, recalculating: false, saving: false, baseChanged: false, pursueChanged: false, autoRecalculateTimer: null,
      fertilizers: { N: [], P: [], K: [] },
      baseValues: { N: {}, P: {}, K: {} }, pursueValues: { N: {}, P: {}, K: {} }
    }
  },
  computed: {
    farmId() { return this.$route.query.farmId || localStorage.getItem('Farm-Id') },
    selectedLandName() { const land = this.lands.find(item => item.id === this.landId); return land ? land.name : '' },
    isValidScheme() { return this.scheme && this.scheme.totalNPK && this.scheme.fertBases && this.scheme.fertPursues },
    fertilizerCards() { return [{ key: 'base', title: '基肥', values: this.baseValues }, { key: 'pursue', title: '追肥', values: this.pursueValues }] },
    periods() { return (this.scheme && this.scheme.fertPursues && this.scheme.fertPursues.fertPeriods) || [] },
    totalWaterCount() { return this.periods.reduce((sum, item) => sum + Number(item.waterNum || 0), 0) }
  },
  created() { this.loadLands() },
  methods: {
    async loadLands() {
      if (!this.farmId) { this.loaded = true; this.$message.error('缺少 farmId，请在地址中传入 farmId'); return }
      try { this.lands = await getLand({ farmId: this.farmId }) || [] } catch (error) { this.$message.error('地块加载失败，请检查 Cookie 或网络') } finally { this.loaded = true }
    },
    async onLandChange() {
      this.yieldId = ''; this.yields = []
      const land = this.lands.find(item => item.id === this.landId)
      if (!land || !land.cropName) { this.$message.error('该地块未绑定作物信息'); return }
      try { const result = await yieldFindAll({ cropName: land.cropName, pageSize: 100, nowPage: 1 }); this.yields = result.content || [] } catch (error) { this.$message.error('目标产量加载失败') }
    },
    async createScheme() {
      if (this.creating) return
      if (!this.landId || !this.yieldId) { this.$message.error('请先选择地块和目标产量'); return }
      this.creating = true
      this.errorMsg = ''
      try {
        const result = await createLandScheme({ landId: this.landId, yieldId: this.yieldId })
        const scheme = result && (result.data || result)
        if (!scheme || !scheme.totalNPK || !scheme.fertBases || !scheme.fertPursues) throw new Error((result && (result.message || result.msg)) || '服务未返回有效方案')
        this.scheme = scheme; this.setFertilizerValues(); this.baseChanged = false; this.pursueChanged = false
        this.loadFertilizers()
      } catch (error) { this.errorMsg = this.getErrorMessage(error); this.$message.error(this.errorMsg) } finally { this.creating = false }
    },
    async loadFertilizers() {
      try {
        const result = await fertilizerFindAll({ fertilizerName: '', typeName: '', pageSize: 100, nowPage: 1 })
        this.fertilizers = { N: [], P: [], K: [] }
        ;(result.content || []).forEach(item => { const key = ['N', 'P', 'K'][item.typeId - 1]; if (key) this.fertilizers[key].push(item) })
        this.matchFertilizerIds(this.baseValues); this.matchFertilizerIds(this.pursueValues)
      } catch (error) { this.$message.error('肥料列表加载失败，稍后可重新生成方案') }
    },
    setFertilizerValues() {
      const data = this.scheme
      this.baseValues = this.makeValues(data.fertBases, 'base')
      this.pursueValues = this.makeValues(data.fertPursues, 'pursue')
    },
    makeValues(source, prefix) { return ['N', 'P', 'K'].reduce((result, key) => { result[key] = { amount: this.range(source[prefix + 'Min' + key], source[prefix + 'Max' + key]), id: '' }; return result }, {}) },
    setAmounts(values, source, prefix) { ['N', 'P', 'K'].forEach(key => { values[key].amount = this.range(source[prefix + 'Min' + key], source[prefix + 'Max' + key]) }) },
    matchFertilizerIds(values) { ['N', 'P', 'K'].forEach(key => { const defaults = { N: '尿素', P: '磷酸一铵', K: '氯化钾' }; const match = this.fertilizers[key].find(item => item.fertilizerName === defaults[key]); values[key].id = match && match.id }) },
    async recalculate() {
      this.recalculating = true
      try {
        if (this.baseChanged) { const data = await updateBase({ nId: this.baseValues.N.id, pId: this.baseValues.P.id, kId: this.baseValues.K.id }); this.setAmounts(this.baseValues, data, 'base') }
        if (this.pursueChanged) { const data = await updateTopdressing({ nId: this.pursueValues.N.id, pId: this.pursueValues.P.id, kId: this.pursueValues.K.id }); this.setAmounts(this.pursueValues, data, 'pursue'); this.scheme.fertPursues.fertPeriods = data.fertPeriods || [] }
        this.baseChanged = false; this.pursueChanged = false; this.$message.success('计算完成')
      } catch (error) { this.$message.error('重新计算失败') } finally { this.recalculating = false }
    },
    markFertilizerChanged(key) {
      this[key + 'Changed'] = true
      clearTimeout(this.autoRecalculateTimer)
      this.autoRecalculateTimer = setTimeout(() => this.recalculate(), 300)
    },
    getErrorMessage(error) {
      const data = error && error.response && error.response.data
      if (error && error.code === 'ECONNABORTED') return '方案生成超时，请重试'
      if (data && typeof data === 'object') return data.message || data.msg || '方案生成失败，请重试'
      return (error && error.message) || '方案生成失败，请重试'
    },
    async saveScheme() { this.saving = true; try { await wfschemeSave(); this.$message.success('保存成功') } catch (error) { this.$message.error('保存失败') } finally { this.saving = false } },
    formatYield(item) { if (item.yieldMin === -1) return '<' + item.yieldMax + ' kg/亩'; if (item.yieldMax === -1) return '>' + item.yieldMin + ' kg/亩'; return item.yieldMin + '-' + item.yieldMax + ' kg/亩' },
    fertilizerRange(key) { const total = this.scheme && this.scheme.totalNPK; return total ? this.range(total['fertilizerMin' + key], total['fertilizerMax' + key]) : '-' },
    range(min, max) { return min == null || max == null || (Number(min) === 0 && Number(max) === 0) ? '-' : min + '-' + max },
    value(value) { return value == null ? '-' : value }
  }
}
</script>

<style scoped>
.scheme-page { height: 100vh; min-height: 100vh; padding: max(14px, env(safe-area-inset-top)) 14px 96px; overflow-y: auto; -webkit-overflow-scrolling: touch; touch-action: pan-y; box-sizing: border-box; color: #26384a; background: #f2f7fc; text-align: left; }
.selection-card, .nutrient-card, .fertilizer-card, .water-card, .period-card { margin-bottom: 14px; padding: 17px; border: 1px solid #e5eef7; border-radius: 14px; background: #fff; box-shadow: 0 5px 16px rgba(70, 119, 159, .08); } .section-heading { margin-bottom: 16px; padding-left: 10px; border-left: 3px solid #5ba1e6; } .eyebrow { color: #5ba1e6; font-size: 11px; font-weight: 700; letter-spacing: 1px; } h2 { margin: 3px 0 0; color: #26384a; font-size: 18px; } .selection-card label { display: block; margin-bottom: 15px; color: #526578; font-size: 13px; font-weight: 600; } .selection-card .el-select { display: block; width: 100%; margin-top: 8px; } .selection-card .el-button { width: 100%; height: 44px; margin-top: 2px; border-color: #5ba1e6; background: #5ba1e6; font-size: 16px; font-weight: 600; } .request-error { margin: 10px 0 0; color: #d84a3f; font-size: 12px; line-height: 1.5; }
.scheme-title { padding: 4px 4px 14px; } .scheme-title span, .scheme-title b, .scheme-title small { display: block; } .scheme-title span { color: #2d6fae; font-size: 20px; font-weight: 700; } .scheme-title b { margin-top: 5px; color: #41566b; font-size: 14px; font-weight: 600; } .scheme-title small { margin-top: 3px; color: #91a2b3; }
.card-caption { display: flex; align-items: center; justify-content: space-between; margin-bottom: 11px; } .card-caption span { color: #2c4054; font-size: 17px; font-weight: 700; } .card-caption small { color: #9aaab9; font-size: 11px; }
.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; } .summary-grid div { min-width: 0; padding: 12px 4px; border: 1px solid #deebf7; border-radius: 10px; background: #f5faff; text-align: center; } .summary-grid span, .summary-grid small { display: block; color: #71879c; font-size: 10px; white-space: nowrap; } .summary-grid strong { display: block; margin: 7px 0 2px; overflow: hidden; color: #347fc5; font-size: 15px; text-overflow: ellipsis; }
.fertilizer-row { display: block; padding: 14px 0; border-top: 1px solid #edf2f7; } .fertilizer-row:first-of-type { border-top: 0; } .fertilizer-row > div > span { display: block; margin-bottom: 7px; color: #5c7184; font-size: 13px; font-weight: 600; } .fertilizer-row .el-select { width: 100%; } .fertilizer-amount { display: flex; align-items: baseline; gap: 7px; margin-top: 9px; padding: 10px 11px; color: #347fc5; border-radius: 8px; background: #f2f8fe; } .fertilizer-amount small { color: #73899e; font-size: 12px; font-weight: 400; } .fertilizer-amount b { margin-left: auto; font-size: 20px; line-height: 1; } .fertilizer-amount em { color: #71869a; font-size: 12px; font-style: normal; font-weight: 400; }
.water-card { display: flex; justify-content: space-around; border-color: #d9eaf9; background: #f8fbff; text-align: center; } .water-card span, .water-card small { display: block; color: #71879c; font-size: 12px; } .water-card strong { display: block; margin: 6px 0 3px; color: #347fc5; font-size: 20px; }
.period-row { padding: 13px 0; border-top: 1px solid #edf2f7; } .period-row h3 { margin: 0 0 8px; color: #41576b; font-size: 14px; } .period-row div { display: flex; justify-content: space-between; gap: 12px; margin-top: 6px; color: #7d91a4; font-size: 12px; } .period-row b { color: #40576d; font-weight: 500; text-align: right; } .action-bar { position: fixed; z-index: 3; right: 0; bottom: 0; left: 0; display: flex; gap: 8px; padding: 12px 14px calc(12px + env(safe-area-inset-bottom)); border-top: 1px solid #e5eef7; background: rgba(255,255,255,.97); box-shadow: 0 -3px 14px rgba(62, 105, 143, .08); } .action-bar .el-button { flex: 1; padding-right: 6px; padding-left: 6px; } .action-bar .el-button--success { border-color: #5ba1e6; background: #5ba1e6; } .empty { padding: 42px 0; color: #8b9dad; text-align: center; }
::v-deep .el-input__inner { height: 42px; border-color: #dbe6f0; color: #40556a; border-radius: 8px; background: #fff; } ::v-deep .el-input__inner:focus { border-color: #5ba1e6; box-shadow: 0 0 0 2px rgba(91, 161, 230, .12); } ::v-deep .el-select .el-input.is-focus .el-input__inner { border-color: #5ba1e6; } ::v-deep .el-button--primary { border-color: #5ba1e6; background: #5ba1e6; }
</style>
